import { type Chapter, type Lens, type Manifest, type MediaItem, formatCounts, formatRange, formatTime, selectBest } from '@seemantham/core';
import { background, stageWidth, stillKey, tileWidth } from '../lib/data';
import { ChevronRight, Moon, Play, Sun } from './icons';

interface HomeProps {
  manifest: Manifest;
  lens: Lens;
  onLens: (lens: Lens) => void;
  onOpenChapter: (chapter: Chapter) => void;
  onOpenItem: (item: MediaItem) => void;
  onOpenPerson: (personId: string) => void;
  resumeItem?: MediaItem;
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
  wide: boolean;
}

const LENSES: Array<{ id: Lens; label: string }> = [
  { id: 'day', label: 'The day' },
  { id: 'best', label: 'Best' },
  { id: 'people', label: 'People' },
];

export function Tabs({ lens, onLens }: { lens: Lens; onLens: (lens: Lens) => void }) {
  return (
    <div className="tabs" role="tablist" aria-label="Ways into the day">
      {LENSES.map((entry) => (
        <button
          key={entry.id}
          type="button"
          role="tab"
          className="tab"
          aria-selected={lens === entry.id}
          onClick={() => onLens(entry.id)}
        >
          {entry.label}
        </button>
      ))}
    </div>
  );
}

function itemById(manifest: Manifest, id?: string): MediaItem | undefined {
  return id ? manifest.items.find((item) => item.id === id) : undefined;
}

function ChapterRows({ manifest, onOpenChapter }: Pick<HomeProps, 'manifest' | 'onOpenChapter'>) {
  return (
    <div>
      {manifest.chapters.map((chapter) => {
        const cover = itemById(manifest, chapter.coverId);
        return (
          <button key={chapter.id} type="button" className="row" onClick={() => onOpenChapter(chapter)}>
            <span
              className="thumb"
              aria-hidden="true"
              style={{ background: background(cover?.thumb, stillKey(cover, 112)) }}
            />
            <span>
              <span className="row__title">{chapter.name ?? formatRange(chapter.startsAt, chapter.endsAt)}</span>
              <span className="row__meta">{formatCounts(chapter.clipCount, chapter.photoCount)}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function BestGrid({ manifest, onOpenItem }: Pick<HomeProps, 'manifest' | 'onOpenItem'>) {
  const best = selectBest(manifest.items);
  if (!best.length) {
    return <p className="note">Nothing is marked yet. The photographer's picks appear here once they are in.</p>;
  }
  return (
    <div className="grid">
      {best.map((item) => (
        <button key={item.id} type="button" className="card" onClick={() => onOpenItem(item)}>
          <span
            className="card__image"
            style={{ background: background(item.thumb, stillKey(item, tileWidth())), position: 'relative' }}
          >
            {item.kind === 'clip' ? (
              <span className="badge">
                <Play />
              </span>
            ) : null}
          </span>
          <span className="card__caption">{formatTime(item.capturedAt)}</span>
        </button>
      ))}
    </div>
  );
}

function PeopleList({ manifest, onOpenPerson }: Pick<HomeProps, 'manifest' | 'onOpenPerson'>) {
  if (!manifest.people.length) {
    return <p className="note">Once you name the people in curate mode, they appear here.</p>;
  }
  return (
    <div>
      {manifest.people.map((person) => {
        const sample = manifest.items.find((item) => item.people?.includes(person.id));
        return (
          <button key={person.id} type="button" className="people-row" onClick={() => onOpenPerson(person.id)}>
            <span
              className="people-row__face"
              aria-hidden="true"
              style={{ background: background(sample?.thumb, person.faceKey) }}
            />
            <span>
              <span className="row__title">{person.name}</span>
              <span className="row__meta">{person.photoCount} photos</span>
            </span>
          </button>
        );
      })}
      <p className="note">Only people you have named appear here.</p>
    </div>
  );
}

export function Home(props: HomeProps) {
  const { manifest, lens, onLens, resumeItem, theme, onToggleTheme, wide } = props;
  const cover = itemById(manifest, manifest.chapters[0]?.coverId) ?? manifest.items[0];

  const body = (
    <>
      {lens === 'day' ? (
        <>
          {resumeItem ? (
            <button type="button" className="row row--continue" onClick={() => props.onOpenItem(resumeItem)}>
              <span className="dot" aria-hidden="true" />
              <span>Continue at {formatTime(resumeItem.capturedAt)}</span>
              <ChevronRight />
            </button>
          ) : null}
          <ChapterRows manifest={manifest} onOpenChapter={props.onOpenChapter} />
        </>
      ) : null}
      {lens === 'best' ? <BestGrid manifest={manifest} onOpenItem={props.onOpenItem} /> : null}
      {lens === 'people' ? <PeopleList manifest={manifest} onOpenPerson={props.onOpenPerson} /> : null}
    </>
  );

  if (wide) {
    return (
      <div className="app app--wide">
        <aside className="sidebar">
          <h1 className="sidebar__title">{manifest.event.title}</h1>
          <p className="sidebar__sub">
            {manifest.event.subtitle}
            <br />
            {manifest.event.date}
          </p>
          <Tabs lens={lens} onLens={onLens} />
          <div className="chapter-list">
            {manifest.chapters.map((chapter, index) => (
              <button
                key={chapter.id}
                type="button"
                className="chapter-list__row"
                aria-current={index === 0}
                onClick={() => props.onOpenChapter(chapter)}
              >
                <span>{chapter.name ?? formatRange(chapter.startsAt, chapter.endsAt)}</span>
                <span>{chapter.clipCount + chapter.photoCount}</span>
              </button>
            ))}
          </div>
          <button type="button" className="row--continue note" onClick={onToggleTheme}>
            {theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
          </button>
        </aside>
        <main className="main">
          <h2 className="main__title">
            {manifest.chapters[0]
              ? formatRange(manifest.chapters[0].startsAt, manifest.chapters[0].endsAt)
              : manifest.event.date}
          </h2>
          <p className="main__sub">
            {manifest.counts.photos} photos and {manifest.counts.clips} clips, in the order they happened
          </p>
          {body}
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <header
        className="hero"
        style={{ background: background(cover?.thumb, stillKey(cover, stageWidth())) }}
        role="img"
        aria-label="Photo from the morning"
      >
        <button
          type="button"
          className="hero__theme"
          onClick={onToggleTheme}
          aria-label={theme === 'dark' ? 'Switch to the light theme' : 'Switch to the dark theme'}
        >
          {theme === 'dark' ? <Sun /> : <Moon />}
        </button>
        <div className="hero__veil">
          <h1 className="hero__title">{manifest.event.title}</h1>
          <p className="hero__sub">
            {manifest.event.subtitle} · {manifest.event.date}
          </p>
        </div>
      </header>
      <Tabs lens={lens} onLens={onLens} />
      {body}
    </div>
  );
}
